import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Info, Search, X, Navigation, ExternalLink } from 'lucide-react';
import { facilities, categories } from './data/facilities';

const DiscountMap = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('map');
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const filteredFacilities = facilities.filter(f =>
    (selectedCategory === 'all' || f.category === selectedCategory) &&
    (searchTerm === '' || f.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCategoryData = (category) => {
    return categories.find(c => c.id === category) || categories[0];
  };

  // Leafletライブラリの読み込み
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.async = true;
      script.onload = () => {
        setMapLoaded(true);
      };
      document.body.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // 地図の初期化
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    const L = window.L;

    try {
      const map = L.map(mapRef.current, {
        center: [36.6428, 138.1883],
        zoom: 12,
        scrollWheelZoom: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18,
      }).addTo(map);

      mapInstanceRef.current = map;

      setTimeout(() => {
        addMarkers(L, map);
      }, 100);
    } catch (error) {
      console.error('地図の初期化エラー:', error);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [mapLoaded]);

  const addMarkers = (L, map) => {
    if (!L || !map) return;

    markersRef.current.forEach(marker => {
      try {
        marker.remove();
      } catch (e) { }
    });
    markersRef.current = [];

    filteredFacilities.forEach(facility => {
      const categoryData = getCategoryData(facility.category);

      const icon = L.divIcon({
        html: `<div style="background: ${categoryData.markerColor}; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; font-size: 18px; cursor: pointer;">${categoryData.icon}</div>`,
        className: '',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      try {
        const marker = L.marker([facility.lat, facility.lng], { icon })
          .addTo(map);

        marker.bindPopup(`
          <div style="min-width: 200px;">
            <div style="font-size: 16px; font-weight: bold; margin-bottom: 6px;">${facility.name}</div>
            <div style="color: #dc2626; font-weight: bold; margin-bottom: 4px; font-size: 14px;">💰 ${facility.discount}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">📍 ${facility.address}</div>
            <button onclick="window.showFacilityDetail(${facility.id})" style="background: #3b82f6; color: white; padding: 6px 12px; border-radius: 6px; border: none; cursor: pointer; font-size: 12px; width: 100%;">詳細を見る</button>
          </div>
        `, {
          maxWidth: 300,
        });

        marker.on('click', () => {
          map.setView([facility.lat, facility.lng], 15);
        });

        markersRef.current.push(marker);
      } catch (error) {
        console.error('マーカー追加エラー:', error);
      }
    });
  };

  useEffect(() => {
    window.showFacilityDetail = (id) => {
      const facility = facilities.find(f => f.id === id);
      if (facility) {
        setSelectedFacility(facility);
      }
    };
  }, []);

  useEffect(() => {
    if (window.L && mapInstanceRef.current) {
      addMarkers(window.L, mapInstanceRef.current);
    }
  }, [selectedCategory, searchTerm, mapLoaded]);

  const showOnMap = (facility) => {
    setViewMode('map');
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView([facility.lat, facility.lng], 15);
        setSelectedFacility(facility);
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="h-screen flex flex-col">
        {/* ヘッダー */}
        <header className="bg-white shadow-lg p-4 z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 truncate">長野市 障害者手帳割引施設マップ</h1>
                <p className="text-gray-600 text-sm">障害者手帳で利用できる割引施設・優待情報ガイド</p>
              </div>
            </div>

            <div className="relative mb-3">
              <label htmlFor="facility-search" className="sr-only">施設名で検索</label>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="facility-search"
                type="text"
                placeholder="施設名で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            <nav className="flex gap-2 overflow-x-auto pb-2" aria-label="カテゴリフィルタ">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  aria-pressed={selectedCategory === cat.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${selectedCategory === cat.id
                      ? cat.color + ' text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <span className="text-lg" aria-hidden="true">{cat.icon}</span>
                  <span className="text-sm">{cat.name}</span>
                  {selectedCategory === cat.id && cat.id !== 'all' && (
                    <span className="ml-1 px-2 py-0.5 bg-white bg-opacity-30 rounded-full text-xs">
                      {filteredFacilities.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setViewMode('map')}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${viewMode === 'map'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                🗺️ 地図表示
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${viewMode === 'list'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                📋 リスト表示
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden relative">
          {viewMode === 'map' ? (
            <section className="w-full h-full" aria-label="インタラクティブマップ">
              <div ref={mapRef} className="w-full h-full"></div>
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">地図を読み込み中...</p>
                  </div>
                </div>
              )}
            </section>
          ) : (
            <section className="h-full overflow-y-auto p-4" aria-label="施設一覧リスト">
              <div className="max-w-4xl mx-auto">
                <header className="mb-4">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-500" />
                    施設一覧
                    <span className="text-base text-gray-500 font-normal">（{filteredFacilities.length}件）</span>
                  </h2>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredFacilities.map(facility => {
                    const categoryData = getCategoryData(facility.category);
                    return (
                      <article
                        key={facility.id}
                        className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => showOnMap(facility)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 bg-gray-50" aria-hidden="true">
                            {categoryData.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base text-gray-800 mb-1 truncate">{facility.name}</h3>
                            <address className="text-xs text-gray-600 mb-2 line-clamp-1 not-italic">{facility.address}</address>
                            <div className="flex gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${categoryData.color} text-white`}>
                                {categoryData.name}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full font-bold bg-red-100 text-red-700">
                                {facility.discount}
                              </span>
                            </div>
                          </div>
                          <Navigation className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" aria-label="地図で見る" />
                        </div>
                      </article>
                    );
                  })}
                </div>

                {filteredFacilities.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl">
                    <p className="text-gray-500 text-lg">該当する施設が見つかりませんでした</p>
                  </div>
                )}
              </div>
            </section>
          )}
        </main>

        {selectedFacility && (
          <aside className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]" onClick={() => setSelectedFacility(null)} role="dialog" aria-labelledby="detail-title">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <header className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-gray-50" aria-hidden="true">
                    {getCategoryData(selectedFacility.category).icon}
                  </div>
                  <h3 id="detail-title" className="text-xl font-bold text-gray-800">{selectedFacility.name}</h3>
                </div>
                <button
                  onClick={() => setSelectedFacility(null)}
                  aria-label="閉じる"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </header>

              <div className="space-y-4">
                <section>
                  <h4 className="text-sm text-gray-500 mb-1">カテゴリ</h4>
                  <span className={`inline-block text-sm px-4 py-2 rounded-lg font-medium ${getCategoryData(selectedFacility.category).color} text-white`}>
                    {getCategoryData(selectedFacility.category).name}
                  </span>
                </section>

                <section>
                  <h4 className="text-sm text-gray-500 mb-1">割引内容</h4>
                  <p className="text-xl font-bold text-red-600">{selectedFacility.discount}</p>
                </section>

                <section>
                  <h4 className="text-sm text-gray-500 mb-1">住所</h4>
                  <address className="text-gray-700 not-italic">{selectedFacility.address}</address>
                </section>

                {selectedFacility.website && (
                  <section>
                    <h4 className="text-sm text-gray-500 mb-1">公式サイト</h4>
                    <a
                      href={selectedFacility.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>公式ホームページを見る</span>
                    </a>
                  </section>
                )}

                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>ご利用時の注意:</strong><br />
                    障害者手帳の提示が必要です。施設によっては付添人も割引対象となる場合があります。詳細は各施設にお問い合わせください。
                  </p>
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default DiscountMap;